/**
 * Error surface. Non-2xx responses from grpc-gateway carry a google.rpc.Status
 * JSON body: `{ "code": <grpc code int>, "message": "...", "details": [...] }`.
 */
const ERROR_INFO_TYPE = 'type.googleapis.com/google.rpc.ErrorInfo';
/**
 * `ErrorInfo.metadata` off the wire, keeping only string values.
 *
 * The field is `map<string, string>` in `google.rpc`, so a non-string value is a producer bug, not
 * a shape this client should absorb. Dropping such an entry is the same choice made for `reason`
 * and `domain` directly above, and for the same reason: **coercing would invent a value.** A
 * consumer reading `metadata.remaining` wants a number it can show an operator or a user, and
 * `String(someObject)` is `'[object Object]'` — a confident wrong answer, which is worse on a
 * degraded-entitlement surface than an absent one.
 *
 * Returns `undefined` rather than `{}` when there is nothing usable, so `metadata` is absent from
 * the result instead of present-and-empty — an `ErrorInfo` carrying only unusable values should be
 * indistinguishable from one carrying none.
 *
 * ⚠ Two DIFFERENT lines do that, and I first credited the wrong one. The `!raw` guard is what makes
 * a detail with no `metadata` field yield `undefined` — that is the case every pre-existing
 * `toEqual({reason, domain})` assertion in this repo exercises, so those tests are unaffected by
 * this change regardless of the final line. The `length > 0` line only covers `{}` and
 * all-values-non-string. Mutation confirmed the split: returning `out` unconditionally reds exactly
 * two cases and leaves the pre-existing assertions green. Worth the correction because "this line
 * protects those tests" was a plausible sentence about a line that does not.
 */
function coerceMetadata(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw))
        return undefined;
    const out = {};
    for (const [k, v] of Object.entries(raw)) {
        if (typeof v === 'string')
            out[k] = v;
    }
    return Object.keys(out).length > 0 ? out : undefined;
}
export class ApiError extends Error {
    /** HTTP status of the response (0 for network-level failures). */
    httpStatus;
    /** gRPC status code from the google.rpc.Status body, when present. */
    grpcCode;
    details;
    constructor(message, httpStatus, grpcCode, details) {
        super(message);
        this.name = 'ApiError';
        this.httpStatus = httpStatus;
        this.grpcCode = grpcCode;
        this.details = details;
    }
    /**
     * True when the app should RE-AUTHENTICATE: HTTP 401 or gRPC UNAUTHENTICATED(16).
     * Deliberately NOT 403 — PERMISSION_DENIED(7)→403 is routinely a business deny
     * on this surface (e.g. CloneAgent's uniform deny for a bad share token, by
     * design), and bouncing those users into OIDC would be wrong.
     */
    get isAuthError() {
        return this.httpStatus === 401 || this.grpcCode === 16;
    }
    /**
     * The google.rpc.ErrorInfo (reason + domain) from the Status details, if any.
     * Consumers match on the (domain, reason) TUPLE — e.g. a mini-app routes
     * (gateway.agnivo.org, MINIAPP_TGUID_MISSING) to the reopen surface. Returns
     * undefined when no ErrorInfo detail is present.
     */
    get errorInfo() {
        for (const d of this.details ?? []) {
            if (d && typeof d === 'object' && d['@type'] === ERROR_INFO_TYPE) {
                const { reason, domain, metadata } = d;
                return {
                    reason: typeof reason === 'string' ? reason : undefined,
                    domain: typeof domain === 'string' ? domain : undefined,
                    metadata: coerceMetadata(metadata),
                };
            }
        }
        return undefined;
    }
}
export function isApiError(err) {
    return err instanceof ApiError;
}
/**
 * ADR-092 AGENT_NOT_AVAILABLE — the gateway's IN-STREAM typed refusal
 * (gateway!160, architect ruling 2026-07-19). The gateway writes the SSE
 * header BEFORE calling agent-core, so a refusal can only arrive as an
 * in-stream error event: `code == AGENT_NOT_AVAILABLE_CODE` +
 * `agentNotAvailableReason` (raw wire enum-NAME string), NOT a pre-stream
 * ApiError. See stream.ts (ReplyEvent 'error').
 */
export const AGENT_NOT_AVAILABLE_CODE = 'REPLY_ERROR_CODE_AGENT_NOT_AVAILABLE';
/**
 * The CLOSED reason vocabulary = the proto AgentNotAvailableReason enum-name
 * strings the wire actually carries. Pinned to the runtime, NOT the (larger,
 * speculative) old HTTP set: agent-core emits exactly {deleted, fallback,
 * not_provisioned} (server.ts:687-693) → anaReasonToProto → these three
 * (gateway replystream.go:725-734). UNSPECIFIED is deliberately EXCLUDED so an
 * unknown/absent reason (a future agent-core cause, or a value we don't model)
 * falls to generic honest copy — the enum default-case the ADR-092 carve-out
 * requires. Product copy set (2026-07-16) maps 1:1 onto the three; the old
 * `AGENT_UNAUTHORIZED` bucket was never a real emitted reason (dropped).
 */
export const AGENT_NOT_AVAILABLE_REASONS = [
    'AGENT_NOT_AVAILABLE_REASON_DELETED',
    'AGENT_NOT_AVAILABLE_REASON_FALLBACK',
    'AGENT_NOT_AVAILABLE_REASON_NOT_PROVISIONED',
];
/**
 * Validate a RAW wire reason string against the known set — the in-stream ANA
 * carrier delivers `agentNotAvailableReason` as an enum-name string. Returns
 * the typed reason, or undefined (→ generic copy) for an unknown/absent value.
 */
export function agentNotAvailableReason(reason) {
    return AGENT_NOT_AVAILABLE_REASONS.includes(reason ?? '')
        ? reason
        : undefined;
}
/** The gateway's ErrorInfo domain. A reason is only unique WITHIN its domain,
 *  so every tuple below is qualified — never match a bare reason string. */
export const GATEWAY_ERROR_DOMAIN = 'gateway.agnivo.org';
/**
 * Does this error carry the gateway tuple (GATEWAY_ERROR_DOMAIN, reason)?
 *
 * A predicate rather than an exported reason constant, because the thing worth
 * making hard to get wrong is the TUPLE match. `err.errorInfo?.reason === X`
 * type-checks, reads naturally, and silently drops the domain — the exact
 * mistake the comment above warns about, and one no test catches while the
 * gateway is the only producer we see.
 *
 * Domain-scoped on purpose: it answers only for gateway reasons. Another
 * producer's domain needs its own predicate, not a `domain` parameter here —
 * a general (domain, reason) matcher would invite callers to pass a domain they
 * got from the error itself, which is a tautology, not a check.
 */
export function isGatewayReason(err, reason) {
    const info = err.errorInfo;
    return info?.domain === GATEWAY_ERROR_DOMAIN && info.reason === reason;
}
/** agent-core's own ErrorInfo domain (`ERROR_INFO_DOMAIN` in agent-core's
 *  `errorInfoStatus.ts`) — a SEPARATE producer from the gateway, so its
 *  reasons need their own domain-scoped predicate, not `isGatewayReason`. */
export const AGENT_CORE_ERROR_DOMAIN = 'agent-core.agnivo.org';
/** Does this error carry the agent-core tuple (AGENT_CORE_ERROR_DOMAIN, reason)?
 *  Same tuple-match discipline as `isGatewayReason` — see that doc for why a
 *  bare `err.errorInfo?.reason === X` is the mistake this guards against. */
export function isAgentCoreReason(err, reason) {
    const info = err.errorInfo;
    return info?.domain === AGENT_CORE_ERROR_DOMAIN && info.reason === reason;
}
/**
 * tenant-service's own ErrorInfo domain (`errorInfoDomain` in tenant-service's
 * `internal/port/grpc/errorinfo.go`) — a THIRD, separate producer from the gateway and
 * agent-core. Routes whose refusal is generated in tenant-service and forwarded
 * VERBATIM by the gateway (never re-stamped — e.g. `GoalWebServer.passGoalStatus`,
 * `tenant-service/internal/port/grpc/goal_service.go`'s `statusWithReason` dispatch)
 * carry this domain on the wire, not `GATEWAY_ERROR_DOMAIN`. Confirmed by reading both
 * services on `dev` 2026-09-27, not inferred from either service's own doc comment.
 */
export const TENANT_ERROR_DOMAIN = 'tenant.agnivo.org';
/** Does this error carry the tenant-service tuple (TENANT_ERROR_DOMAIN, reason)?
 *  Same tuple-match discipline as `isGatewayReason` — see that doc for why a
 *  bare `err.errorInfo?.reason === X` is the mistake this guards against. */
export function isTenantReason(err, reason) {
    const info = err.errorInfo;
    return info?.domain === TENANT_ERROR_DOMAIN && info.reason === reason;
}
/**
 * `AgentLimitHitError`'s reason (agent-core `runtime/agentConfig.ts`,
 * `AGENT_LIMIT_HIT = 'agent_limit_hit'`) — RESOURCE_EXHAUSTED, `metadata` carries
 * `max_agents` / `plan_id` / `upgrade_cta_url` as strings (`ErrorInfo.metadata`
 * is `map<string,string>`; parse `max_agents` as int at the call site).
 */
export const REASON_AGENT_LIMIT_HIT = 'agent_limit_hit';
/**
 * ADR-092 addressing-gate reasons the CLIENT has a recovery for. Not the full
 * gate vocabulary — `AGENT_ID_REQUIRED` and `TGUID_MALFORMED` are deliberately
 * absent, having no client-side fix.
 *
 * `CONVERSATION_AGENT_MISMATCH`: the send named a conversation that is not the
 * addressed agent's derived one (gateway `deriveAddressing`). The derivation is
 * a pure function of (tg_uid, agent_id), so this says the client's cached id is
 * stale relative to the current token — re-resolve, then the same bytes are
 * accepted. Pre-persist on the gateway side ("nothing is written on a send that
 * never happened"), so the retry reuses the ORIGINAL idempotency key.
 */
export const REASON_CONVERSATION_AGENT_MISMATCH = 'CONVERSATION_AGENT_MISMATCH';
/**
 * The remaining ADR-092 addressing-gate reasons, which have NO client-side
 * recovery. Exported so a surface can give each its own copy — the refusals
 * differ in what the user should do (nothing / go to Telegram / reopen), and one
 * generic string gets at least one of them wrong.
 *
 * These are CLASS A: the gateway's `message` for them is English developer prose
 * written for a log reader (`addressingRefusal`, gateway-service
 * `internal/adapter/grpc/addressing.go`), so a client MUST supply its own copy.
 * Contrast CLASS B — the filepolicy reasons under this SAME domain, where
 * `rejectStatus` puts product's already-rendered, localized, variable-resolved
 * copy in the message and a client must RENDER it rather than replace it.
 *
 * The domain does not separate the two classes and neither does the casing
 * (gate reasons are UPPER_SNAKE; `filepolicy` and `ability_tier_locked` are
 * lower_snake, all under `gateway.agnivo.org`). So enumerate the Class-A
 * reasons explicitly, as here, rather than matching on a pattern — a pattern
 * would silently swallow a Class-B reason and replace good copy with worse.
 */
export const REASON_ADDRESSING_NOT_DERIVABLE = 'ADDRESSING_NOT_DERIVABLE';
export const REASON_AGENT_ID_REQUIRED = 'AGENT_ID_REQUIRED';
export const REASON_TGUID_MALFORMED = 'TGUID_MALFORMED';
/**
 * (domain, reason) tuples whose refusal is SERVER STATE rather than a property
 * of the request: the SAME bytes succeed once that state changes.
 *
 * This is a fact about the ERROR, not about any one call — a reason means what
 * it means whichever request provoked it — which is why it lives here beside
 * `errorInfo` rather than in a screen. Contrast a caller's own set of "which
 * refusals are reachable on MY path", which is path-scoped and must stay with
 * the path (frontend/developer review of web!297, 2026-08-10).
 *
 * PINNED TO THE RUNTIME, same discipline as AGENT_NOT_AVAILABLE_REASONS above:
 * each member cites its emitting layer so a reader can check completeness.
 * A reason allowlist with no producer citation cannot be audited.
 *
 *   IDENTITY_NOT_PROVISIONED
 *     gateway-service `internal/adapter/middleware/auth/ingressjwterror.go`
 *     (`MapIngressJWTError` → `identityNotProvisionedErr`), FailedPrecondition(9),
 *     REST 400. Valid Zitadel token, no platform `users` row yet (ADR-089
 *     FINDING-4). Raised in the ingress-JWT MIDDLEWARE, so it rides EVERY
 *     request from an OIDC surface — sends, agent lists, history reads, uploads
 *     — not only the ADR-092-addressed ones. Remedy: the user opens the bot, the
 *     row appears, the identical request succeeds. Telling them to reload is the
 *     one action that provably does not help.
 */
/**
 * The reason string itself, exported for the same reason `httpStatusMessage` is: one
 * producer shape, one definition. A consumer classifying this refusal needs the literal
 * to pass to `isGatewayReason`, and a second copy of it in an app is a silent drift risk
 * — the kind that reads correct forever and stops matching the day the gateway renames.
 *
 * Exported as the bare REASON, not the `domain|reason` key: the key shape is this
 * module's internal index, while the tuple match callers must perform is
 * `isGatewayReason(err, REASON_IDENTITY_NOT_PROVISIONED)`, which supplies the domain
 * itself. Handing out the joined key would invite a `errorInfo.reason === key` compare
 * that can never be true.
 */
export const REASON_IDENTITY_NOT_PROVISIONED = 'IDENTITY_NOT_PROVISIONED';
/**
 * 025 S7 — `goals.confirmMilestone`/`rejectMilestone`'s state-race reason (`client.ts`'s
 * own docblock): a genuinely different concurrent answer to the same
 * MILESTONE_PROPOSED event. An IDENTICAL repeat is retry-safe and succeeds silently —
 * this reason is only ever raised for a real race, never for a caller's own retry.
 */
export const REASON_PROPOSAL_ALREADY_ANSWERED = 'PROPOSAL_ALREADY_ANSWERED';
const REPLAYABLE_ERROR_REASONS = new Set([
    `${GATEWAY_ERROR_DOMAIN}|${REASON_IDENTITY_NOT_PROVISIONED}`,
]);
/**
 * Can this exact request succeed if sent again UNCHANGED? The question a retry
 * affordance must answer before it is offered: the retry replays identical
 * bytes (same payload, same idempotency key under never-replay dedupe), so a
 * refusal of the request AS FORMED can only be refused again.
 *
 * Answers from the ERROR alone. Deliberately does NOT consult the gRPC code:
 * a code's answer depends on which layer produced it, and only the caller knows
 * which producers its own path can reach. Callers combine this with their own
 * path-scoped default — check this FIRST, since an enumerated reason overrides
 * whatever the code would have implied (both are needed: IDENTITY_NOT_PROVISIONED
 * is code 9 AND REST 400, so a code-only classifier catches it twice).
 *
 * Returns undefined when the error carries no ErrorInfo tuple, or carries one we
 * have not enumerated — i.e. "no answer from the reason", NOT "not replayable".
 * A boolean here would make an unknown reason indistinguishable from a known
 * non-replayable one and silently override the caller's default.
 *
 * `true | undefined` and NOT `boolean | undefined` — narrowed so "never false" is
 * CHECKED rather than documented (developer review, 2026-08-10). The body cannot
 * currently produce false, so the wider type was a true statement about the
 * implementation asserted as a property of the exported contract. It also fails
 * open in the wrong direction: adding `return false` for an explicitly
 * non-replayable reason would type-check silently, and every `!== undefined`
 * caller would start reading it as a real negative verdict — a semantic change
 * with no compiler event. This forces that conversation the day someone needs it.
 */
export function replayableByReason(err) {
    const { domain, reason } = err.errorInfo ?? {};
    if (!domain || !reason)
        return undefined;
    return REPLAYABLE_ERROR_REASONS.has(`${domain}|${reason}`) ? true : undefined;
}
/**
 * The message an ApiError falls back to when the response body carries no usable
 * `message`. Exported ONLY so the one producer shape has one definition: both
 * error constructors build it, and `serverRenderedMessage` has to be able to
 * RECOGNISE it. A literal duplicated into the recogniser would go silently inert
 * the day someone reformats either producer — the failure being that a
 * placeholder gets rendered to a user as if it were product copy.
 */
export function httpStatusMessage(httpStatus) {
    return `HTTP ${httpStatus}`;
}
const SERVER_COPY_MARKER_KEY = 'copy';
const SERVER_COPY_MARKER_VALUE = 'server';
/**
 * (domain, reason) tuples whose STATUS MESSAGE is already user-facing product
 * copy — rendered server-side, localized from `x-language-code`, with every
 * variable resolved. For these the client's job is to DISPLAY the message, not
 * to key its own string off the reason.
 *
 * This is the CLASS B set named in the Class-A block above, and the two are not
 * separable by anything on the wire: same domain, and casing does not sort them
 * either (see the denominator note below). So it is an explicit enumeration,
 * and a pattern would fail in both directions with neither detectable from the
 * client — matching a Class-A reason renders English developer prose to a
 * Russian user, missing a Class-B reason throws away copy product wrote.
 *
 * A client-side string for these would be strictly WORSE than what already
 * arrives: `file_size_exceeded`'s copy interpolates `{max_size_mb}` from the
 * caller's effective plan, which the client does not know. It would also pass
 * its own tests.
 *
 * PINNED TO THE RUNTIME, same discipline as REPLAYABLE_ERROR_REASONS above —
 * an allowlist with no producer citation cannot be audited:
 *
 *   files_disabled / type_not_allowed / vision_disabled / file_size_exceeded /
 *   count_exceeded
 *     gateway-service `internal/domain/filepolicy/filepolicy.go` (RejectReason,
 *     the complete set as of origin/dev 0ab53ff) → `sendmessage.go` `rejectStatus`,
 *     which sets `status.message = refusalcopy.Render(reason, planID, locale, vars)`
 *     and attaches ErrorInfo{reason, domain}. InvalidArgument(3), REST 400.
 *     PRE-STREAM: `replystream.go` refuses before the SSE header, so these arrive
 *     as a thrown ApiError and never as an in-stream error event.
 *
 *   ability_tier_locked
 *     gateway-service `internal/domain/clientagentadmin/clientagentadmin.go`
 *     `fastFailAllowlistWrite` — gateway-service!217 (origin/dev `0d38316b`)
 *     routes this refusal through `refusalcopy.Render` with the plan-tier
 *     upgrade copy (RU + EN, plan-aware, no `/upgrade` CTA — agent-admin is
 *     web-only). FailedPrecondition(9), REST 400. Previously a hardcoded
 *     English literal at the same call site; tracked on
 *     `2026-08-10-developer-gateway-ability-tier-locked-unlocalized.md`.
 *
 * The earlier c022e98 "ability_tier_locked is absent because it is un-localized
 * today" paragraph (and its ⚠️ WHEN IT MOVES clause) is RETIRED: that reason
 * moved into this set the day `refusalcopy.Render` started emitting it.
 * An absent reason here would silently discard freshly-localized copy — the
 * failure direction with no symptom. If a future reason is added, it gets the
 * same producer citation + an entry here the same day.
 *
 * Keep it enumerated rather than pattern-matched, and note the argument is
 * stronger than "casing does not sort the classes" (developer's correction on
 * web!305 — casing very nearly does). Across all thirteen reasons under this
 * domain at `0d38316b`: the seven SCREAMING_SNAKE are all Class A; the six
 * lower_snake are these six Class-B reasons. **A casing heuristic is wrong 6
 * of 13** — worse than one that fails often, because it passes a test suite
 * built from the filepolicy set, ships, and mis-renders exactly the one reason
 * it gets wrong (now: every non-filepolicy lower_snake reason).
 */
const SERVER_RENDERED_COPY_REASONS = new Set([
    `${GATEWAY_ERROR_DOMAIN}|files_disabled`,
    `${GATEWAY_ERROR_DOMAIN}|type_not_allowed`,
    `${GATEWAY_ERROR_DOMAIN}|vision_disabled`,
    `${GATEWAY_ERROR_DOMAIN}|file_size_exceeded`,
    `${GATEWAY_ERROR_DOMAIN}|count_exceeded`,
    `${GATEWAY_ERROR_DOMAIN}|ability_tier_locked`,
    // Free photo quota (api inbox 2026-10-18T16-01Z): gateway-rendered RU/EN copy, Class-B like vision_disabled.
    // Lockstep with the gateway-side quota enforcement, which stays dark until this is on dev.
    `${GATEWAY_ERROR_DOMAIN}|photo_quota_exceeded`,
    `${GATEWAY_ERROR_DOMAIN}|photo_quota_unavailable`,
    // Storage quota (api inbox 2026-10-25T1440Z, ADR-199 D10-7): same shape as the photo-quota pair.
    // Gateway-side rendering stays dark until file-storage!166 + flags; the entry must be on dev first.
    `${GATEWAY_ERROR_DOMAIN}|storage_quota_exceeded`,
    `${GATEWAY_ERROR_DOMAIN}|storage_quota_unavailable`,
    // file-storage's second cap (api inbox 2026-10-26T0242Z, gateway-service!572); dark until enforcement is armed.
    `${GATEWAY_ERROR_DOMAIN}|storage_footprint_exceeded`,
]);
/**
 * @internal The reasons that are in BOTH sets above — an intersection that must stay EMPTY, and
 * is now a checked property instead of an undocumented accident.
 *
 * Why the intersection is load-bearing, concretely: `admin-tenant`'s `saveRefusal` composes the
 * two predicates with `replayableByReason(e) === true` FIRST (its own docblock and this module's
 * both say why — an enumerated reason overrides what a code would imply). The day a reason joins
 * both sets, that ordering makes the composition silently wrong BOTH ways for that reason: the
 * replayable check returns `true`, `saveRefusal` returns `null` before ever consulting
 * `serverRenderedMessage`, and the operator gets generic «Не удалось сохранить. Попробуйте ещё
 * раз» copy over a REFUSAL the gateway already rendered product copy for — plus a «Повторить»
 * button that replays identical bytes into an identical refusal. web!367 kept the
 * replayable-check-first line with a disclosed 0-red row precisely because nothing pinned this;
 * architect's ruling (`45c57ef5`): pin the invariant where it lives, not where it is consumed.
 *
 * Exported as a DERIVED intersection rather than exporting the two sets themselves: the sets are
 * this module's internal index (the docblock on `REASON_IDENTITY_NOT_PROVISIONED` says why the
 * joined-key shape is not handed out), and a derived value gives the test exactly the one fact it
 * asserts without widening the surface to "import and diff the enumerations". Tree-shakes out of
 * client bundles — pure derivation, unreferenced by any runtime path. The `@internal` tag is
 * documentation, not enforcement; nothing here strips it from `dist`, and that is accepted.
 */
export const REPLAYABLE_AND_SERVER_RENDERED = [...REPLAYABLE_ERROR_REASONS].filter((key) => SERVER_RENDERED_COPY_REASONS.has(key));
/**
 * The user-facing copy this error CARRIES, or undefined if it carries none.
 *
 * Undefined means "show your own copy", and every caller needs that branch: it
 * covers an unenumerated reason, no ErrorInfo at all, and — the case worth
 * naming — an enumerated reason whose message never made it. Both ApiError
 * producers substitute `httpStatusMessage()` for an absent body message, and
 * they disagree on empty-string (`transport.ts` keeps `''` via `??`, `stream.ts`
 * falls back via a truthiness test), so BOTH degenerate forms are reachable and
 * both would render as a refusal reason. Rejected here rather than at each call
 * site, because a caller that forgets looks fine until the day a message is
 * missing.
 *
 * Returns the trimmed message — the gateway renders it for display, so leading
 * or trailing whitespace is transport noise, not copy.
 */
export function serverRenderedMessage(err) {
    const { domain, reason, metadata } = err.errorInfo ?? {};
    if (!domain || !reason)
        return undefined;
    // The gateway sets `metadata.copy = "server"` only on `refusalcopy.Render` output and scrubs the key from
    // every ErrorInfo it did not build (gateway-service!573), so the marker is authoritative under its own
    // domain. The enumerated set stays as a one-release fallback for a gateway that predates the marker.
    const marked = domain === GATEWAY_ERROR_DOMAIN && metadata?.[SERVER_COPY_MARKER_KEY] === SERVER_COPY_MARKER_VALUE;
    if (!marked && !SERVER_RENDERED_COPY_REASONS.has(`${domain}|${reason}`))
        return undefined;
    const message = err.message.trim();
    if (!message || message === httpStatusMessage(err.httpStatus))
        return undefined;
    return message;
}
//# sourceMappingURL=errors.js.map